import { useEffect, useRef } from "react";
import gsap from "gsap";
import type { LoaderProps } from "./types";

const R = 84;
const CIRC = 2 * Math.PI * R;

/**
 * Fallback premium loader for themes that haven't shipped their own. A dark
 * screen with a hairline ring that fills with preload progress, the couple's
 * title, and an explicit "GENERIC LOADER" tag so it's obvious this theme's
 * loader hasn't been designed yet. Receives the shared `LoaderProps`.
 */
export function GenericLoader({ progress, ready, title, accent, bg, label, onExited }: LoaderProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const ringWrapRef = useRef<HTMLDivElement>(null);
  const pct = Math.round(Math.min(1, Math.max(0, progress)) * 100);

  useEffect(() => {
    if (!ready) return;
    const overlay = overlayRef.current;
    if (!overlay) { onExited(); return; }
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    const tl = gsap.timeline({ onComplete: onExited });
    if (reduce) {
      tl.to(overlay, { opacity: 0, duration: 0.45, ease: "power2.out" });
    } else {
      tl.to(ringWrapRef.current, { scale: 1.06, opacity: 0, duration: 0.6, ease: "power2.in" }, 0)
        .to(overlay, { opacity: 0, duration: 0.6, ease: "power2.inOut" }, 0.15);
    }
    return () => { tl.kill(); };
  }, [ready, onExited]);

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center text-white"
      style={{ background: bg, fontFamily: "'Inter', system-ui, sans-serif" }}
      role="status"
      aria-live="polite"
      aria-busy={!ready}
    >
      <div ref={ringWrapRef} className="relative flex items-center justify-center" style={{ width: 196, height: 196 }}>
        <svg width="196" height="196" viewBox="0 0 196 196" className="absolute inset-0">
          <circle cx="98" cy="98" r={R} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
          <circle
            cx="98"
            cy="98"
            r={R}
            fill="none"
            stroke={accent}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeDasharray={CIRC}
            strokeDashoffset={CIRC * (1 - Math.min(1, Math.max(0, progress)))}
            transform="rotate(-90 98 98)"
            style={{ transition: "stroke-dashoffset 0.4s ease" }}
          />
        </svg>
        <span className="text-[10px] tabular-nums tracking-[0.3em]" style={{ color: accent }}>
          {String(pct).padStart(2, "0")}
        </span>
      </div>

      <p
        className="mt-8 font-light text-center px-6"
        style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(1.5rem, 6vw, 2.25rem)" }}
      >
        {title}
      </p>
      {label && <p className="mt-3 text-[10px] uppercase tracking-[0.5em] text-white/40">{label}</p>}

      {/* Explicit placeholder marker — this theme's loader isn't designed yet. */}
      <p className="mt-10 text-[9px] uppercase tracking-[0.45em] text-white/30">Generic Loader</p>

      <span className="sr-only">Yükleniyor… %{pct}</span>
    </div>
  );
}
